import React, { useState, useEffect } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import './ZoneSlotDrag.css';

const ZoneSlotDrag = ({
  label,
  fieldMap,
  setFieldMap,
  hand,
  setHand,
  mainDeck,
  extraDeck,
  banished,
  setBanished,
  setExtraDeck,
  setMainDeck,
  graveyard,
  setGraveyard,
  // Panel control props
  setShowDeckPanel,
  setShowExtraDeckPanel,
  setShowGYPanel,
  setShowBZPanel,
  setShowXyzPanel,
  setXyzMaterials,
  setXyzMaterialsZone,
  setXyzPanelZone,
  xyzMaterials,
  xyzMaterialsZone,
  xyzPanelZone,
  zoneRef,
  // Card info modal handlers
  setSelectedCard,
  setShowCardInfoModal,
  isDragging: globalIsDragging,
}) => {
  // XYZ modal state removed - using expandable panel instead

  const zoneCard = fieldMap[label]?.card;

  // Check if this zone should show flip options (MZ or STZ zones, but not Link Monsters)
  const isFlipableZone = (zoneLabel) => {
    const hasMZ = zoneLabel.includes('MZ');
    const hasSTZ = zoneLabel.includes('STZ');
    const isZoneFlipable = hasMZ || hasSTZ;
    
    // Check if the card is a Link Monster (which can't be set face-down)
    const isLinkMonster = zoneCard?.type?.toLowerCase().includes('link');
    
    const isFlipable = isZoneFlipable && !isLinkMonster;
    
    console.log(`🔍 Zone ${zoneLabel} is flipable: ${isFlipable} - MZ: ${hasMZ}, STZ: ${hasSTZ}, LinkMonster: ${isLinkMonster}, ZoneCard: ${zoneCard ? 'exists' : 'none'}`);
    return isFlipable;
  };

  // Check if a zone is draggable
  const isDraggableZone = (zoneLabel) => {
    return !['My Deck', 'My Extra', 'My GYZ', 'My BZ'].includes(zoneLabel);
  };

  // Handle card flipping
  const handleCardFlip = (flipType) => {
    if (!zoneCard || !isFlipableZone(label)) return;

    const updatedFieldMap = { ...fieldMap };
    const currentZone = updatedFieldMap[label];
    
    // Ensure the card has the isFaceDown property initialized
    if (currentZone.card.isFaceDown === undefined) {
      currentZone.card.isFaceDown = false;
    }
    
    if (flipType === 'facedown') {
      currentZone.card = { ...currentZone.card, isFaceDown: true };
    } else if (flipType === 'faceup') {
      currentZone.card = { ...currentZone.card, isFaceDown: false };
    }
    
    setFieldMap(updatedFieldMap);
    console.log(`Card flipped ${flipType} in zone ${label}:`, currentZone.card.name);
  };

  // ============================================================================
  // SIMPLIFIED DND-KIT IMPLEMENTATION
  // ============================================================================
  // 
  // This component now only handles the visual representation and basic drag/drop setup.
  // The complex logic has been moved to DuelDragPage for centralized management.
  //
  // Key changes:
  // 1. **Simplified Drop Handling**: No more complex useEffect or handleCardDrop
  // 2. **Centralized Logic**: All card movement is handled in DuelDragPage.onDragEnd
  // 3. **Cleaner Code**: Component focuses on UI and basic drag/drop setup
  // 4. **Better Performance**: No unnecessary re-renders from complex state logic
  //
  // The component provides:
  // - useDroppable: Makes the zone a drop target with ID "drop-{label}"
  // - useDraggable: Makes cards in the zone draggable with ID "drag-{label}-{cardId}"
  // - Visual feedback: Shows drag state and drop highlighting
  // ============================================================================

  // Drop target for this zone - Simplified for dnd-kit
  const { setNodeRef: setDropRef, isOver, active } = useDroppable({
    id: `drop-${label}`, // This ID is used by the main drag handler
    data: {
      zone: label,
      accepts: ['card', 'xyz-material'],
    },
  });

  // Drag source for cards in this zone - Simplified for dnd-kit
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `drag-${label}-${zoneCard?.id || 'empty'}`,
    data: {
      card: zoneCard,
      from: 'field',
      fromZone: label,
      cardId: zoneCard?.id,
    },
    disabled: !zoneCard || !isDraggableZone(label),
  });

  // Remove the complex drop handling - now handled centrally in DuelDragPage
  // The useEffect and handleCardDrop are no longer needed here

  // Remove the old canAcceptCard function and related logic since validation is now centralized
  // The component now only handles the visual representation and basic drag/drop setup

  const sendXYZMaterialsToGY = (card) => {
    if (card?.type?.includes('XYZ') && Array.isArray(card.materials)) {
      const detachedMaterials = card.materials.map(material => {
        // Clean each material to remove any nested materials they might have
        const cleanedMaterial = { ...material, isFaceDown: false };
        delete cleanedMaterial.materials;
        return cleanedMaterial;
      });
      if (detachedMaterials.length > 0) {
        setGraveyard(prev => [...prev, ...detachedMaterials]);
      }
    }
  };

  return (
    <div
      ref={(node) => {
        setDropRef(node);
        // Apply the zone ref for positioning
        if (zoneRef) {
          zoneRef.current = node;
        }
      }}
      className={`zone zone-${label.replace(/\s+/g, '-')} ${isOver && active ? 'glow' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: isOver && active ? 'rgba(255, 255, 255, 0.1)' : undefined,
        minHeight: '80px',
        minWidth: '60px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Display card if there's one in this zone */}
      {(() => {
        if (zoneCard) {
          const isXYZWithMaterials = zoneCard?.type?.toLowerCase().includes('xyz') && zoneCard.materials?.length > 0;

          return (
            <>
              <div 
                ref={setDragRef}
                {...attributes}
                {...listeners}
                className={`xyz-card-wrapper ${isXYZWithMaterials ? 'xyz-card-pile' : ''}`} 
                style={{ position: 'relative' }}
              >
                <img
                  src={
                    (zoneCard.isFaceDown || false)
                      ? require('../assets/images/card-back.jpg')
                      : zoneCard.card_images?.[0]?.image_url
                  }
                  alt={zoneCard.name}
                  className={`zone-card-image ${zoneCard.position === 'DEF' ? 'defense' : ''} ${['My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10'].includes(label) ? 'opponent-style' : ''}`}
                  style={{
                    transform: (zoneCard.isFaceDown || false) && label.includes('MZ') ? 'rotate(90deg)' : 'none'
                  }}
                  data-dragging={isDragging}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Only show card info modal if not dragging and not an XYZ card with materials
                    if (!isDragging && !isXYZWithMaterials) {
                      setSelectedCard(zoneCard);
                      setShowCardInfoModal(true);
                    } else if (isXYZWithMaterials) {
                      setXyzMaterials(zoneCard.materials);
                      setXyzMaterialsZone(label);
                      setXyzPanelZone(label);
                      setShowXyzPanel(true);
                    }
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                />
                
                {/* Face-up/Face-down indicator */}
                {isFlipableZone(label) && (
                  <div 
                    className={`card-state-indicator ${(zoneCard.isFaceDown || false) ? 'face-down' : 'face-up'}`}
                    title={(zoneCard.isFaceDown || false) ? 'Face-down - Click UP button to flip face-up' : 'Face-up - Click DOWN button to flip face-down'}
                  />
                )}

                {/* Stack appearance for materials */}
                {isXYZWithMaterials && zoneCard.materials.map((mat, idx) => (
                  <img
                    key={`material-${mat.id}`} // Use only card ID for stable React keys
                    src={require('../assets/images/card-back.jpg')}
                    alt="material"
                    className="xyz-material-layer"
                    style={{ top: `${(idx + 1) * 2}px`, left: `${(idx + 1) * 2}px` }}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ))}

                {/* Material count badge */}
                {zoneCard?.materials?.length > 0 && (
                  <span className="material-count-badge">
                    {zoneCard.materials.length}
                  </span>
                )}

                {/* Flip buttons for MZ and STZ zones */}
                {isFlipableZone(label) && (
                  <div className="flip-buttons-container">
                    {/* Show Flip Face-Up button when card is face-down */}
                    {(zoneCard.isFaceDown || false) && (
                      <button
                        className="flip-button flip-faceup active"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardFlip('faceup');
                        }}
                        title="Flip card face-up to reveal"
                      >
                        <span className="button-text">UP</span>
                      </button>
                    )}
                    
                    {/* Show Flip Face-Down button when card is face-up */}
                    {!(zoneCard.isFaceDown || false) && (
                      <button
                        className="flip-button flip-facedown active"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardFlip('facedown');
                        }}
                        title="Flip card face-down to hide"
                      >
                        <span className="button-text">DOWN</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* XYZ modal removed - using expandable panel instead */}
            </>
          );
        }

        // Show back image for Deck and Extra Deck if not empty
        if (label === 'My Deck' && mainDeck?.length > 0) {
          return (
            <div
              className="zone-card-wrapper clickable-zone"
              onClick={() => {
                console.log('Deck clicked, setting showDeckPanel to true');
                setShowDeckPanel(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={require('../assets/images/card-back.jpg')}
                alt="Deck"
                className="zone-card-image"
                onContextMenu={(e) => e.preventDefault()}
              />
              <span className="deck-count-badge">{mainDeck.length}</span>
            </div>
          );
        }

        if (label === 'My Extra' && extraDeck?.length > 0) {
          return (
            <div
              className="zone-card-wrapper clickable-zone"
              onClick={() => {
                console.log('Extra deck clicked, setting showExtraDeckPanel to true');
                setShowExtraDeckPanel(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={require('../assets/images/card-back.jpg')}
                alt="Extra Deck"
                className="zone-card-image"
                onContextMenu={(e) => e.preventDefault()}
              />
              <span className="deck-count-badge">{extraDeck.length}</span>
            </div>
          );
        }

        if (label === 'My GYZ' && graveyard?.length > 0) {
          const topCard = graveyard[graveyard.length - 1];
          const stackSize = Math.min(3, graveyard.length); // show up to 3 stacked cards
          console.log('Rendering GYZ:', { graveyardLength: graveyard.length, stackSize });

          return (
            <div
              className="zone-card-wrapper gy-stack clickable-zone"
              onClick={() => {
                console.log('GYZ clicked, setting showGYPanel to true');
                setShowGYPanel(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              {[...Array(stackSize)].map((_, idx) => {
                const offset = (stackSize - idx - 1) * 4; // overlap
                const card = graveyard[graveyard.length - stackSize + idx];
                console.log('Rendering GYZ card:', { idx, card: card?.name, offset });
                return (
                  <NonDraggableCard
                    key={`gy-card-${card.id}`} // Use only card ID for stable React keys
                    card={card}
                    style={{ position: 'absolute', top: offset + 'px', left: offset + 'px', zIndex: idx }}
                  />
                );
              })}
              <span className="deck-count-badge">{graveyard.length}</span>
            </div>
          );
        }

        if (label === 'My BZ' && banished?.length > 0) {
          const stackSize = Math.min(3, banished.length); // show up to 3 stacked cards
          console.log('Rendering BZ:', { banishedLength: banished.length, stackSize });

          return (
            <div
              className="zone-card-wrapper gy-stack clickable-zone"
              onClick={() => {
                console.log('BZ clicked, setting showBZPanel to true');
                setShowBZPanel(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              {[...Array(stackSize)].map((_, idx) => {
                const offset = (stackSize - idx - 1) * 4;
                const card = banished[banished.length - stackSize + idx];
                return (
                  <NonDraggableCard
                    key={`bz-card-${card.id}`} // Use only card ID for stable React keys
                    card={card}
                    style={{ position: 'absolute', top: offset + 'px', left: offset + 'px', zIndex: idx }}
                  />
                );
              })}
              <span className="deck-count-badge">{banished.length}</span>
            </div>
          );
        }

        return null;
      })()}
    </div>
  );
};

// DraggableCard component for cards in graveyard/banished stacks (used in expandable panels)
const DraggableCard = ({ card, from, index, style }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `drag-${from}-${card.id}-${index}`, // Use both card ID and index for unique identification
    data: {
      card,
      from,
      index,
      cardId: card.id, // Unique card identifier for stable tracking
    },
  });

  return (
    <img
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      src={card.card_images?.[0]?.image_url}
      alt={card.name}
      className="zone-card-image"
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
        transform: transform ? `translate3d(${transform.x || 0}px, ${transform.y || 0}px, 0)` : undefined,
      }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
};

// NonDraggableCard component for cards displayed in zones (not draggable)
const NonDraggableCard = ({ card, style }) => {
  return (
    <img
      src={card.card_images?.[0]?.image_url}
      alt={card.name}
      className="zone-card-image"
      style={{
        ...style,
        pointerEvents: 'auto', // Allow hover cursor to show
        cursor: 'pointer', // Show pointer cursor when hovering over cards
      }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
};

export default ZoneSlotDrag;
